import React, { FC, useEffect, useRef } from 'react';
import { CommonKeys } from '@components/EntityDrawers/constants/keys';
import useSourceNode from '@components/EntityDrawers/hooks/useSourceNode';
import ControlledTextField, {
  ControlledTextFieldProps,
} from '@components/Inputs/controllers/ControlledTextField';
import { useProject } from '@context/Project/ProjectProvider';

type Props = Pick<
  ControlledTextFieldProps,
  'label' | 'placeholder' | 'multiline'
> & {
  editMode?: boolean;
};

/**
 * Name field for the entity drawers.
 *
 * Takes focus for an entity that has no name yet, and once more when the drawer
 * was opened carrying `autoFocusName` — the hand-off from a shortcut create into
 * the edit drawer. That flag is read into a ref and cleared on mount, because
 * `autoFocus` only applies at mount and the tab subtree remounts on every switch
 * between Standard and Advanced: left set, the caret would jump back here each
 * time the user returned to the first tab.
 */
const NameInput: FC<Props> = ({ editMode, multiline = true, ...props }) => {
  const { viewOnly, drawer, setDrawer } = useProject();
  const activeNode = useSourceNode();

  const requestedFocus = useRef(Boolean(drawer?.payload?.autoFocusName));

  const shouldFocusName =
    !activeNode?.data?.dto?.name || requestedFocus.current;

  useEffect(() => {
    if (!drawer || !drawer.payload?.autoFocusName) return;

    setDrawer({
      ...drawer,
      payload: { ...drawer.payload, autoFocusName: false },
    });
  }, []);

  return (
    <ControlledTextField
      name={CommonKeys.Name}
      helperText={!editMode && "Entity may be saved by entering it's name only"}
      required
      disabled={viewOnly}
      autoFocus={shouldFocusName}
      autoComplete="off"
      multiline={multiline}
      {...props}
    />
  );
};

export default NameInput;
